import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Calendar, dateFnsLocalizer, Navigate } from 'react-big-calendar';
import TimeGrid from 'react-big-calendar/lib/TimeGrid';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import addDays from 'date-fns/addDays';
import addHours from 'date-fns/addHours';
import isAfter from 'date-fns/isAfter';
import subDays from 'date-fns/subDays';
import isWithinInterval from 'date-fns/isWithinInterval'; // 用來檢查日期範圍
import zhTW from 'date-fns/locale/zh-TW';
import { getManagerStaffList, getManagerRoster, saveManagerRosterBatch } from '../../services/api';

// 2. 設定 date-fns localizer
const locales = {
  'zh-TW': zhTW,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const minTime = new Date();
minTime.setHours(10, 0, 0);
const maxTime = new Date();
maxTime.setHours(23, 30, 0);

// ==========================================
// 🌟 自訂視圖：RollingWeek
// ==========================================
const RollingWeek = ({ date, localizer, ...props }) => {
  const range = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(date, i));
    }
    return days;
  }, [date]);

  return <TimeGrid {...props} range={range} eventOffset={15} localizer={localizer} />;
};

RollingWeek.range = (date) => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    days.push(addDays(date, i));
  }
  return days;
};

RollingWeek.navigate = (date, action) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return addDays(date, -1);
    case Navigate.NEXT:
      return addDays(date, 1);
    case Navigate.TODAY:
      return subDays(new Date(), 3);
    default:
      return date;
  }
};

RollingWeek.title = (date, { localizer }) => {
  return localizer.format(date, 'yyyy年 MM月');
};

// Event 靚仔啲, 時間一行, title 一行
const CustomEvent = ({ event }) => {
  // 因為你之前個 createEvent 已經將 title 寫成 "陳大文 1100-1500"
  // 如果想分開顯示，可以係度做字串處理，或者之後改返 createEvent 只存人名
  
  // 假設 event.title 依家係 "陳大文 1100-1500"
  // 我地淨係想要人名部分 (假設用空格分開)
  const nameOnly = event.title.split(' ')[0]; 

  return (
    <div className="h-full flex flex-col justify-center leading-none">
      {/* 上面：時間 (細字、Bold) */}
      <div className="text-[10px] font-bold opacity-90 mb-0.5">
        {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
      </div>
      
      {/* 下面：人名 (大少少) */}
      <div className="text-xs font-normal leading-tight whitespace-normal break-words">
        {nameOnly}
      </div>
    </div>
  );
};

// ==========================================
// 🌟 主 Component
// ==========================================
const RosterCalendar = () => {
  const [staffList, setStaffList] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [originalEvents, setOriginalEvents] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  
  // 🌟 新增：Cache State (紀錄目前已經下載了的時間範圍)
  // 格式：{ start: Date, end: Date }
  const [loadedRange, setLoadedRange] = useState({ start: null, end: null });

  // Modal State
  const [showStaffModal, setShowStaffModal] = useState(false);

  //自由選單
  const [formState, setFormState] = useState({
    dateStr: '',    // e.g. '2025-11-24' (用來做 ID 重組日期)
    displayDate: '',// e.g. '11月24日 (一)' (用來顯示)
    startTime: '11:00',
    endTime: '20:00', // 預設收工時間
    staffId: ''
  });

  const TIME_SLOTS = useMemo(() => {
    const slots = [];
    for (let i = 10; i <= 23; i++) {
    const hour = i < 10 ? `0${i}` : i;
    slots.push(`${hour}:00`);
    if (i !== 23) { // 23:30 視乎你收幾點，如果收23:00就唔好加
        slots.push(`${hour}:30`);
    }
    }
    return slots;
  }, []);

  // Date State
  const [date, setDate] = useState(subDays(new Date(), 3));

  const { views, defaultView } = useMemo(() => ({
    views: {
      month: true,
      week: RollingWeek,
      day: true,
    },
    defaultView: 'week'
  }), []);

// 自訂一個日期控件
const CustomToolbar = ({ onNavigate, label, date, view, onView }) => {
  
  // 處理導航
  const goToBack = () => onNavigate(Navigate.PREVIOUS);
  const goToNext = () => onNavigate(Navigate.NEXT);
  const goToToday = () => onNavigate(Navigate.TODAY);

  return (
    <div className="flex items-center justify-between mb-2 p-1 bg-gray-50 rounded-lg border border-gray-200">
      
      {/* 左邊：今日 & View 切換 */}
      <div className="flex items-center gap-1">
        <button
          onClick={goToToday}
          className="px-3 py-1 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded border border-indigo-200 transition-colors"
        >
          今日
        </button>
        
        {/* 如果你想保留切換月/週/日的功能，可以用這個小 Select */}
        {/* <select 
            value={view} 
            onChange={(e) => onView(e.target.value)}
            className="text-xs border-none bg-transparent focus:ring-0 text-gray-600 font-medium"
        >
            <option value="month">月</option>
            <option value="week">週</option>
            <option value="day">日</option>
        </select>
        */}
      </div>

      {/* 右邊：日期 + 左右箭嘴 */}
      <div className="flex items-center gap-2">
        <button
          onClick={goToBack}
          className="p-1 text-gray-500 hover:text-indigo-600 hover:bg-gray-200 rounded-full transition"
        >
          {/* 左箭嘴 Icon */}
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
        </button>

        {/* 日期 Label (e.g. 2025年 11月) */}
        <span className="text-sm sm:text-base font-bold text-gray-800 whitespace-nowrap min-w-[80px] text-center">
          {label}
        </span>

        <button
          onClick={goToNext}
          className="p-1 text-gray-500 hover:text-indigo-600 hover:bg-gray-200 rounded-full transition"
        >
          {/* 右箭嘴 Icon */}
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
        </button>
      </div>
    </div>
  );
 };

  // 🔥 核心優化：智慧 Fetch
  // forceRefresh: 是否強制重新下載 (Save 後用)
  const fetchEvents = useCallback(async (visibleRange = null, forceRefresh = false) => {
    try {
      let targetStart, targetEnd;

      // 1. 計算使用者目前「想看」的範圍
      if (visibleRange) {
         if (Array.isArray(visibleRange)) {
            targetStart = visibleRange[0];
            targetEnd = visibleRange[visibleRange.length - 1];
         } else {
            targetStart = visibleRange.start;
            targetEnd = visibleRange.end;
         }
      } else {
         targetStart = new Date(); 
         targetEnd = addDays(new Date(), 7);
      }

      // 2. 🛡️ 緩存檢查 (Cache Check)
      // 如果不是強制刷新，且想看的範圍完全包含在已下載範圍內，就收工 (0 流量)
      if (!forceRefresh && loadedRange.start && loadedRange.end) {
        const isStartCovered = targetStart >= loadedRange.start;
        const isEndCovered = targetEnd <= loadedRange.end;

        if (isStartCovered && isEndCovered) {
          console.log("🟢 Cache Hit! 資料已在記憶體，無需下載。");
          return; // 直接結束，唔 Call API
        }
      }

      // 3. 🧨 擴大下載範圍 (Big Fetch)
      // 如果要下載，就一次過下載前後 45 日 (共約 3 個月)，食飽佢！
      const apiStart = subDays(targetStart, 45); 
      const apiEnd = addDays(targetEnd, 45);
      
      // 設定時間邊界
      apiStart.setHours(0, 0, 0, 0);
      apiEnd.setHours(23, 59, 59, 999);

      const params = {
          start: apiStart.toISOString(),
          end: apiEnd.toISOString(),
          page_size: 5000, // 確保大範圍數據一次過返黎
      };

      console.log("🚀 Network Request Triggered (Big Chunk):", params); 

      const response = await getManagerRoster(params);
      
      const loadedEvents = response.data.results.map(evt => ({
        ...evt,
        id: evt.id,
        start: new Date(evt.start),
        end: new Date(evt.end),
        resourceId: evt.resourceId
      }));
      
      setEvents(loadedEvents);
      setOriginalEvents(loadedEvents);
      
      // 4. 更新緩存範圍記錄
      setLoadedRange({ start: apiStart, end: apiEnd });

    } catch (error) {
      console.error("Load Events Failed:", error);
    }
  }, [loadedRange]); // 依賴 loadedRange 狀態

  // Navigate
  const onNavigate = useCallback((newDate) => {
    setDate(newDate);
  }, []);

  // Range Change
  const handleRangeChange = useCallback((range) => {
    // 這裡只告訴 fetchEvents 我想看什麼範圍
    // fetchEvents 內部會決定是否真的要去下載
    fetchEvents(range, false); 
  }, [fetchEvents]);

  // Load Staff
  const fetchStaff = async () => {
    try {
      const response = await getManagerStaffList();
      setStaffList(response.data.results);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    }
  };

  // Initial Load
  useEffect(() => {
    fetchStaff();
    const initialStart = date;
    const initialEnd = addDays(date, 6);
    // 第一次載入，當然要下載
    fetchEvents({ start: initialStart, end: initialEnd }, true); 
  }, []); // 只執行一次

  const calendarEvents = useMemo(() => {
    return events.map(event => ({
      ...event,
      start: new Date(event.start),
      end: new Date(event.end),
    }));
  }, [events]);

  //小工具 format 啲date
const formatForInput = (date) => {
  if (!date) return '';
  const offset = date.getTimezoneOffset() * 60000; // 修正時區差
  const localISOTime = (new Date(date - offset)).toISOString().slice(0, 16);
  return localISOTime;
};

  const formats = useMemo(() => ({
      monthHeaderFormat: (date, culture, localizer) => localizer.format(date, 'yyyy年 MM月', culture),
      dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
        `${localizer.format(start, 'yyyy年 MM月dd日', culture)} - ${localizer.format(end, 'MM月dd日', culture)}`,
      dayFormat: (date, culture, localizer) => localizer.format(date, 'MM/dd (eeeee)', culture),
      // 👇👇👇 加入這一行！強制改做 24小時制 (14:00 - 18:00) 👇👇👇
      //eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      //  `${localizer.format(start, 'HHmm', culture)} - ${localizer.format(end, 'HHmm', culture)}`,
      eventTimeRangeFormat: () =>"",
      timeGutterFormat: (date, culture, localizer) => localizer.format(date, 'HH:mm', culture),
      dateFormat: (date, culture, localizer) => localizer.format(date, 'd', culture),
  }), []);

const eventStyleGetter = useCallback((event) => {
    // 1. 在 staffList 裡面找出對應的員工
    const staff = staffList.find(s => String(s.id) === String(event.resourceId));
    
    // 2. 預設顏色 (藍色)
    let backgroundColor = '#3b82f6'; 

    // 3. 如果搵到員工，而且佢有設定 color，就用佢個 color
    if (staff && staff.color) {
        backgroundColor = staff.color;
    }

    // 4. 特殊 Event 覆蓋 (例如 'break' 休息時間固定用橙色)
    if (event.type === 'break') {
        backgroundColor = '#f97316'; 
    }

    return { 
        style: { 
            backgroundColor: backgroundColor, 
            borderRadius: '6px', 
            opacity: 1, // 建議改做 1 或 0.9，顏色會實淨啲，太透會因為疊加變污糟
            color: 'white', // 文字顏色
            border: '0px', 
            display: 'block',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)' // 加少少陰影靚仔啲
        } 
    };
  }, [staffList]);

  const createEvent = (staffId, start, end) => {
    const staff = staffList.find((s) => String(s.id) === String(staffId));
    if (!staff) return;
    const title = `${staff.chiname} ${format(start, 'HHmm')}-${format(end, 'HHmm')}`;
    const newEventObj = {
      id: Math.random().toString(36).substr(2, 9),
      title, start, end, resourceId: staffId, type: 'shift',
    };
    setEvents((prevStrings) => {
      const existingEventsAsDates = prevStrings.map(e => ({ ...e, start: new Date(e.start), end: new Date(e.end) }));
      const otherStaffEvents = existingEventsAsDates.filter((e) => e.resourceId !== staffId);
      const currentStaffEvents = existingEventsAsDates.filter((e) => e.resourceId === staffId);
      const rawList = [...currentStaffEvents, newEventObj];
      const optimizedListDates = mergeAndOptimizeEvents(rawList);
      const finalDateList = [...otherStaffEvents, ...optimizedListDates];
      return finalDateList.map(e => ({ ...e, start: e.start.toISOString(), end: e.end.toISOString() }));
    });
  };

  const mergeAndOptimizeEvents = (eventsToMerge) => {
    if (eventsToMerge.length === 0) return [];
    const sorted = [...eventsToMerge].sort((a, b) => a.start - b.start);
    const merged = [];
    let current = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      if (current.end >= next.start) {
        const newEndTime = new Date(Math.max(current.end, next.end));
        let survivorId = current.id;
        if (String(current.id).includes('_') && !String(next.id).includes('_')) { survivorId = next.id; }

        // --- 修正位置：重新生成 Title ---
        // 提取原本的人名 (去掉舊的時間部分)
        const staffName = current.title.split(' ')[0]; 
        const newTitle = `${staffName} ${format(current.start, 'HHmm')}-${format(newEndTime, 'HHmm')}`;

        current = {
           ...current, 
           id: survivorId, 
           end: newEndTime ,
           title: newTitle // 更新這個字串！
        };
      } else {
        merged.push(current);
        current = next;
      }
    }
    merged.push(current);
    return merged;
  };

  const handleSelectSlot = useCallback(({ start, end, action }) => {

    // 原本的 Drag 邏輯 (如果係拖拉，start 同 end 會唔同)
    if (selectedStaff === 'all') {
        const defaultEnd = addHours(start, 3); // 預設 3 鐘

        // 設定表單初始值
        // 1. 格式化日期 (顯示用 & 邏輯用)
        
        const dateStr = format(start, 'yyyy-MM-dd');
        const displayDate = format(start, 'MM月dd日 (eeeee)', { locale: zhTW });
        console.log("start" + start);
        console.log("dateStr" + dateStr);
        
        // 2. 計算開始時間 (Start Time)
        // 確保 start 至少是 10:00 (Calendar 有時可能點到空白處)
        let startObj = start;
        const minStartObj = new Date(start);
        minStartObj.setHours(10, 0, 0, 0); // 設定當天 10:00

        if (startObj < minStartObj) {
            startObj = minStartObj; // 早過 10:00 就當 10:00
        }

        // 3. 計算結束時間 (End Time) -> 自動 +3 小時
        let endObj = addHours(startObj, 3);

        // 4. 設定當天 23:00 的邊界
        const maxEndObj = new Date(start);
        maxEndObj.setHours(23, 0, 0, 0);

        // 如果 +9 小時後遲過 23:00，就縮返去 23:00
        if (isAfter(endObj, maxEndObj)) {
            endObj = maxEndObj;
        }

        // 5. 轉回字串 "HH:mm" 以便 Dropdown 讀取
        // 注意：Calendar 有時會比 "11:00:00"，format 會出 "11:00" 剛好對應 Dropdown value
        const startTimeStr = format(startObj, 'HH:mm');
        const endTimeStr = format(endObj, 'HH:mm');
        console.log("startTimeStr" + startTimeStr);
        console.log("endTimeStr" + endTimeStr);
        setFormState({
            dateStr,
            displayDate,
            startTime: startTimeStr,
            endTime: endTimeStr,
            staffId: selectedStaff === 'all' ? '' : selectedStaff
        });

       setShowStaffModal(true);
       return;
    } else {
      createEvent(selectedStaff, start, end);
    }
  }, [selectedStaff]);


  const handleSelectEvent = useCallback((event) => {
    const confirmDelete = window.confirm(`🗑️ 確定要刪除這個更表嗎？\n\n標題：${event.title}`);
    if (confirmDelete) {
      setEvents((prevEvents) => prevEvents.filter((e) => e.id !== event.id));
    }
  }, []);

  const handleSave = async () => {
    if (isSaving) return;

    const toCreateData = [];
    const toUpdateData = [];
    const toDeleteIds = [];

    events.forEach(currentEvent => {
      const original = originalEvents.find(e => String(e.id) === String(currentEvent.id));
      if (!original) {
        if (currentEvent.resourceId) {
          toCreateData.push({
              title: currentEvent.title,
              start: new Date(currentEvent.start).toISOString(),
              end: new Date(currentEvent.end).toISOString(),
              resourceId: parseInt(currentEvent.resourceId, 10),
              type: currentEvent.type || 'shift'
          });
        }
      } else {
        const currentStart = new Date(currentEvent.start).getTime();
        const currentEnd = new Date(currentEvent.end).getTime();
        const originalStart = new Date(original.start).getTime();
        const originalEnd = new Date(original.end).getTime();
        const isTimeChanged = currentStart !== originalStart || currentEnd !== originalEnd;
        const isResourceChanged = String(original.resourceId) !== String(currentEvent.resourceId);

        if (isTimeChanged || isResourceChanged) {
          toUpdateData.push({
            id: currentEvent.id,
            title: currentEvent.title,
            start: new Date(currentEvent.start).toISOString(),
            end: new Date(currentEvent.end).toISOString(),
            resourceId: parseInt(currentEvent.resourceId, 10),
            type: currentEvent.type
          });
        }
      }
    });

    originalEvents.forEach(original => {
      const stillExists = events.find(e => String(e.id) === String(original.id));
      if (!stillExists) { toDeleteIds.push(original.id); }
    });

    if (toCreateData.length === 0 && toUpdateData.length === 0 && toDeleteIds.length === 0) {
        alert("沒有變更需要儲存");
        return;
    }

    setIsSaving(true);

    try {
      const payload = { toCreate: toCreateData, toUpdate: toUpdateData, toDelete: toDeleteIds }
      await saveManagerRosterBatch(payload);
      alert('儲存成功！');
      
      // 🌟 Save 完之後，強制 Refresh (True)
      // 確保資料庫最新的 ID 同 Data 同步返黎前端
      await fetchEvents({ start: date, end: addDays(date, 6) }, true);

    } catch (error) {
      console.error('儲存失敗', error);
      alert('儲存失敗，請檢查網絡');
    }finally{
      setIsSaving(false);
    }
  };

// 極致空間壓縮
const IntegratedToolbar = useMemo(() => {
    return ({ onNavigate, label, view, onView }) => {
      const goToBack = () => onNavigate(Navigate.PREVIOUS);
      const goToNext = () => onNavigate(Navigate.NEXT);

      const handleViewChange = (e) => {
        const newView = e.target.value;
        onView(newView);
        onNavigate(Navigate.TODAY);
      };

      return (
        <div className="flex flex-col bg-gray-50 border-b border-gray-200">
          
          {/* 第一層：功能區 (視圖 + 員工 + 儲存) - 解決「儲存放上啲」 */}
          <div className="flex items-center justify-between p-2 gap-2 border-b border-gray-100">
            {/* 左側：視圖切換 */}
            <div className="relative">
                <select
                    value={view}
                    onChange={handleViewChange}
                    className="appearance-none pl-2 pr-6 py-2 bg-indigo-50 border border-indigo-200 rounded-md text-xs font-bold text-indigo-700 focus:outline-none shadow-sm cursor-pointer"
                >
                    <option value="month">📅 月</option>
                    <option value="week">📅 週</option>
                    <option value="day">📅 日</option>
                </select>
            </div>

            {/* 右側：員工選擇 + 儲存 (組合在一起) */}
            <div className="flex items-center gap-1.5">
                <div className="relative">
                    <select
                        value={selectedStaff}
                        onChange={(e) => setSelectedStaff(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 text-gray-700 py-2 px-2 pr-6 rounded text-xs font-medium focus:outline-none shadow-sm max-w-[90px] truncate"
                    >
                        <option value="all">全體</option>
                        {staffList.map((staff) => (
                            <option key={staff.id} value={staff.id}>
                                {staff.chiname}
                            </option>
                        ))}
                    </select>
                </div>

                <button 
                    onClick={handleSave}   
                    disabled={isSaving}
                    className={`px-3 py-2 text-white rounded shadow-sm flex-shrink-0 text-xs font-bold transition-all ${
                        isSaving ? 'bg-gray-400' : 'bg-green-600 active:bg-green-700'
                    }`}
                >
                    {isSaving ? "⏳ 儲存中" : "💾 儲存"}
                </button>
            </div>
          </div>

          {/* 第二層：日期導航區 (左箭頭 | 日期標籤 | 右箭頭) - 確保「不擋住導航」 */}
          <div className="flex items-center w-full bg-white">
               {/* 左箭嘴 */}
               <button 
                 onClick={goToBack} 
                 className="flex-1 flex justify-center items-center py-3 text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100 border-r border-gray-100 transition-colors"
               >
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path>
                 </svg>
               </button>

               {/* 中間日期 Label (移除年份顯示以騰出空間) */}
               <div className="flex-[2] text-center">
                  <span className="text-base font-black text-gray-800 tracking-tight">
                      {label.replace(/202[5-6]年\s?/, '')}
                  </span>
               </div>

               {/* 右箭嘴 */}
               <button 
                 onClick={goToNext} 
                 className="flex-1 flex justify-center items-center py-3 text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100 border-l border-gray-100 transition-colors"
               >
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7"></path>
                 </svg>
               </button>
          </div>
        </div>
      );
    };
  }, [staffList, selectedStaff, handleSave, isSaving]);

  return (
<div className="flex flex-col h-full"> 
      
      <div className="flex-grow relative" style={{ minHeight: '0', touchAction: 'none'}}>
        <Calendar
          date={date}
          className="locked-roster-view"
          onNavigate={onNavigate}
          dayLayoutAlgorithm='no-overlap' 
          culture='zh-TW'
          components={{
            toolbar: IntegratedToolbar,
            event: CustomEvent
          }}
          longPressThreshold={100}
          localizer={localizer}
          formats={formats}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          eventPropGetter={eventStyleGetter}
          views={views}
          defaultView={defaultView}
          step={30}
          timeslots={2}
          min={minTime}
          max={maxTime}
          selectable={true}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          onRangeChange={handleRangeChange}
        />
      </div>

      {/*
      <div className="mt-4 border-t-2 border-gray-200 pt-4">
        <h3 className="text-lg font-bold text-gray-700 mb-2">📊 Debug Data (最新加入在最底)</h3>
        */}
        {/* 顯示目前 Cache 狀態，方便 Debug */}
        {/*<p className="text-xs text-gray-500">Cache Range: {loadedRange.start ? format(loadedRange.start, 'yyyy/MM/dd') : 'None'} - {loadedRange.end ? format(loadedRange.end, 'yyyy/MM/dd') : 'None'}</p>
        <textarea
          className="w-full h-48 p-3 bg-gray-900 text-green-400 font-mono text-sm rounded border border-gray-300 outline-none"
          value={JSON.stringify({ currentViewDate: date, totalEvents: events.length }, null, 2)}
          readOnly
        />
      </div>
      */}

{showStaffModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
            <div 
                className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity"
                onClick={() => setShowStaffModal(false)}
            ></div>

            <div className="bg-white w-full sm:w-96 rounded-t-2xl sm:rounded-xl shadow-2xl transform transition-all overflow-hidden z-10 flex flex-col max-h-[90vh]">
                
                {/* Header: 直接顯示鎖定的日期 */}
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-indigo-50">
                    <div>
                        <span className="text-xs font-bold text-indigo-500 uppercase tracking-wider">新增更表</span>
                        <h3 className="text-xl font-bold text-indigo-900">{formState.displayDate}</h3>
                    </div>
                    <button onClick={() => setShowStaffModal(false)} className="text-gray-400 hover:text-gray-600 p-2">
                        ✕
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    
                    {/* 1. 員工選擇 */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">指派員工</label>
                        <div className="relative">
                            <select
                                value={formState.staffId}
                                onChange={(e) => setFormState({...formState, staffId: e.target.value})}
                                className="w-full appearance-none bg-white border border-gray-300 text-gray-800 py-3 px-4 pr-8 rounded-xl leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500 text-lg font-medium"
                            >
                                <option value="" disabled>-- 請選擇 --</option>
                                {staffList.map((staff) => (
                                    <option key={staff.id} value={staff.id}>
                                        {staff.chiname} ({staff.role})
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                                <svg className="fill-current h-5 w-5" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                            </div>
                        </div>
                    </div>

                    {/* 2. 時間選擇 (並排兩個 Dropdown) */}
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-bold text-gray-700 mb-1">開始</label>
                            <div className="relative">
                                <select 
                                    value={formState.startTime}
                                    onChange={(e) => setFormState({...formState, startTime: e.target.value})}
                                    className="w-full appearance-none bg-gray-50 border border-gray-300 text-gray-800 py-3 px-4 rounded-xl focus:ring-2 focus:ring-indigo-500 text-lg font-bold text-center"
                                >
                                    {TIME_SLOTS.map(t => <option key={`start-${t}`} value={t}>{t}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="flex items-end pb-3 text-gray-400">➜</div>

                        <div className="flex-1">
                            <label className="block text-sm font-bold text-gray-700 mb-1">結束</label>
                            <div className="relative">
                                <select 
                                    value={formState.endTime}
                                    onChange={(e) => setFormState({...formState, endTime: e.target.value})}
                                    className="w-full appearance-none bg-gray-50 border border-gray-300 text-gray-800 py-3 px-4 rounded-xl focus:ring-2 focus:ring-indigo-500 text-lg font-bold text-center"
                                >
                                    {TIME_SLOTS.map(t => <option key={`end-${t}`} value={t}>{t}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 flex gap-3">
                    <button 
                        onClick={() => setShowStaffModal(false)}
                        className="flex-1 px-4 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-xl shadow-sm active:bg-gray-100"
                    >
                        取消
                    </button>
                    <button 
                        onClick={() => {
console.group("🚀 Debug: Add Event Clicked"); // 開一個 Group 方便睇
        console.log("📦 Form State:", formState);

        // 1. 檢查有無選人
        if (!formState.staffId) {
            console.warn("⚠️ Validation Failed: No Staff ID");
            alert("請選擇員工！");
            console.groupEnd();
            return;
        }

        try {
            // 2. 檢查日期字串組合
            const startStr = `${formState.dateStr}T${formState.startTime}:00`;
            const endStr = `${formState.dateStr}T${formState.endTime}:00`;
            console.log("📅 Date String Construction:", { startStr, endStr });

            // 3. 建立 Date Object
            const startObj = new Date(startStr);
            const endObj = new Date(endStr);
            console.log("🕒 Date Objects:", { startObj, endObj });

            // 🚨 重點檢查：手機好容易出 Invalid Date
            if (isNaN(startObj.getTime()) || isNaN(endObj.getTime())) {
                console.error("❌ Critical Error: Invalid Date Detected!");
                console.error("Causes: dateStr or time format might be wrong/empty.");
                alert("日期格式錯誤 (Invalid Date)");
                console.groupEnd();
                return;
            }

            // 4. 檢查時間邏輯
            if (endObj <= startObj) {
                console.warn("⚠️ Logic Error: End <= Start", {
                    startTs: startObj.getTime(),
                    endTs: endObj.getTime()
                });
                alert("結束時間必須遲過開始時間！");
                console.groupEnd();
                return;
            }

            // 5. 呼叫 createEvent
            console.log("✅ Checks Passed. Calling createEvent with:", {
                staffId: formState.staffId,
                start: startObj,
                end: endObj
            });

            createEvent(formState.staffId, startObj, endObj);
            
            console.log("🎉 Success! Closing Modal.");
            setShowStaffModal(false);

        } catch (error) {
            // 捕捉任何隱藏嘅 Error
            console.error("🔥 CRASH inside onClick:", error);
            alert("系統發生錯誤: " + error.message);
        } finally {
            console.groupEnd();
        }
    }}
                        className="flex-1 px-4 py-3 bg-indigo-600 text-white font-bold rounded-xl shadow-md hover:bg-indigo-700 active:bg-indigo-800"
                    >
                        確定
                    </button>
                </div>

            </div>
        </div>
      )}
    </div>
  );
};

export default RosterCalendar;  